import { useState, useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { Channel } from '@annunciate/core/dist/models';
import {
  COLLECTION_GET,
  CONTENT_GET,
  request,
  useCollectionQuery,
  useContentQuery,
} from '@annunciate/query';
import { Collection } from '@annunciate/web/dist/pages/shared';
import { Content } from '@annunciate/web/dist/pages/shared';
import { ROUTE_CONTENT_PREFIX } from '@annunciate/web/dist/utils';

import { withSetup, WithSetupProps } from '@/utils/withSetup';
import { Head } from '@/components/Head';

type ShowProps = {
  id: number | string;
  channelSettings: Channel['settings'];
}

// Collection display page
function CollectionShow({ id, channelSettings }: ShowProps) {
  const { data } = useCollectionQuery(id);
  const item = data?.Collection;

  // SSR FIX: begin
  // this is a fix to resolve the issue where font-sizes, which are based on
  // screen sizes, are not rendered correctly during SSR due to a lack of
  // a window element. Because react doesn't diff the dom on hydration (see link
  // below), once the client render happens the dom fails to update the styles.
  //
  // As a result, we force a re-render by adjusting the key of the component
  // once rendered on the client.
  //
  // https://stackoverflow.com/questions/69049048/styles-not-applying-on-first-client-render-nor-after-when-browser-styles-diffe
  const [key, setKey] = useState<string>('ssr');
  useEffect(() => {
    setKey('client');
  }, []);
  // SSR FIX: end

  return (
    <>
      <Head
        title={item?.name || ''}
        description={item?.description}
        image={item?.cover?.src}
        settings={channelSettings}
      />
      <main>
        <Collection key={key} collectionId={id} />
      </main>
    </>
  )
}

// Content display page
function ContentShow({ id, channelSettings }: ShowProps) {
  const { data } = useContentQuery(id);
  const item: any = data?.Content;

  // SSR FIX: begin
  // this is a fix to resolve the issue where font-sizes, which are based on
  // screen sizes, are not rendered correctly during SSR due to a lack of
  // a window element. Because react doesn't diff the dom on hydration (see link
  // below), once the client render happens the dom fails to update the styles.
  //
  // As a result, we force a re-render by adjusting the key of the component
  // once rendered on the client.
  //
  // https://stackoverflow.com/questions/69049048/styles-not-applying-on-first-client-render-nor-after-when-browser-styles-diffe
  const [key, setKey] = useState<string>('ssr');
  useEffect(() => {
    setKey('client');
  }, []);
  // SSR FIX: end

  return (
    <>
      <Head
        title={item?.name || ''}
        description={item?.description}
        image={item?.cover?.src}
        settings={channelSettings}
      />
      <main>
        <Content key={key} contentId={id}/>
      </main>
    </>
  )
}

// Generic discover page
export default function DiscoverShow({
  id,
  isContent,
  channelSettings,
}: WithSetupProps) {
  if (isContent) {
    return <ContentShow id={id} channelSettings={channelSettings} />
  } else {
    return <CollectionShow id={id} channelSettings={channelSettings} />
  }
}


DiscoverShow.getInitialProps = withSetup(async (
  ctx: any,
  client: QueryClient,
) => {
  const slug: string[] = ctx?.query?.slug || [];
  const id = slug[slug.length - 1];
  const isContent = slug[slug.length - 2] === ROUTE_CONTENT_PREFIX;
  const variables = { id };

  const queryKey = isContent ? 'content' : 'collection';
  const query = isContent ? CONTENT_GET : COLLECTION_GET;
  const getInitialData = async () => {
    return request({
      query,
      variables,
    });
  };

  // this is used for discover
  await client.prefetchQuery({
    queryKey: [queryKey, variables],
    queryFn: getInitialData,
  });

  return { id, isContent };
});
